/** "Emit event" batch dialog — Bookings (`tour_participant.*`) and Orders
 *  (`order.*`) grids, `emit_event` op.
 *
 *  The event list comes from the backend catalog (the codes delivery actually
 *  matches on; never-emitted codes are not offered). Update events and the three
 *  parameterised events ask where their diff / values come from: empty (or
 *  defaults), borrowed from the last emitted event of that code for each record
 *  (optionally narrowed by delivery status and a window), or pasted JSON. A tag
 *  and a reason are mandatory; customer webhooks (off by default) and Force are
 *  offered only where the entry supports them. The dry run is optional and is
 *  reviewed inline — per record, which notifications would match and why the
 *  others would not; editing anything but the reason voids it. Emit asks for
 *  confirmation and runs in the background.
 *
 *  Plan: backend aidocs/plans/2026-09-30-console-batch-emit-events.md.
 */
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  LinearProgress,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { runBatch } from '@/api/batch';
import { ConsoleApiError } from '@/api/client';
import type { EmitEventCatalogEntry, EmitEventGrid, EmitEventSource, EmitEventTaskResult } from '@/api/emitEvents';
import { fetchEmitEventCatalog } from '@/api/emitEvents';
import { AppDialog } from '@/components/AppDialog';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import type { BatchActionContext } from '@/components/DataGrid/batchTypes';
import { HelpLink } from '@/components/HelpLink';
import { useSnackbar } from '@/components/Snackbar';
import { TimeWindowPicker } from '@/components/TimeWindowPicker';
import { useBatchTaskResult } from '@/features/batch-edit/useBatchTaskResult';
import { openBackgroundTask } from '@/features/bgtasks/openBackgroundTask';

import { EmitEventsReport } from './EmitEventsReport';
import {
  EMPTY_DRAFT,
  buildParams,
  findEntry,
  groupLabel,
  jsonTemplate,
  operationIdentity,
  parseJsonObject,
  submitBlocker,
  type EmitDraft,
} from './model';

const SOURCE_LABELS: Record<'diff' | 'params', Record<EmitEventSource, (e: EmitEventCatalogEntry) => string>> = {
  diff: {
    empty: () => 'Empty diff — as if nothing changed',
    borrow: () => 'Borrow the diff of the last emitted update of each record',
    json: () => 'Paste a diff (JSON)',
  },
  params: {
    empty: (e) => `Defaults — ${e.params_defaults ?? 'neutral values'}`,
    borrow: (e) => `Borrow the values of the last emitted “${e.label}” of each record`,
    json: () => 'Paste the values (JSON)',
  },
};

export function EmitEventDialog({ ctx, grid }: { ctx: BatchActionContext; grid: EmitEventGrid }) {
  const snackbar = useSnackbar();
  const noun = grid === 'orders' ? 'order' : 'booking';
  const [draft, setDraft] = useState<EmitDraft>(EMPTY_DRAFT);
  const [dryRunTaskId, setDryRunTaskId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const patch = (p: Partial<EmitDraft>) => setDraft((d) => ({ ...d, ...p }));

  const catalogQuery = useQuery({
    queryKey: ['emit-event-catalog', grid],
    queryFn: () => fetchEmitEventCatalog(grid),
    staleTime: 5 * 60_000,
  });
  const catalog = catalogQuery.data;
  const entry = findEntry(catalog, draft.code);

  const identity = useMemo(() => operationIdentity(catalog, draft, ctx.selection), [catalog, draft, ctx.selection]);
  useEffect(() => {
    setDryRunTaskId(null);
  }, [identity]);
  const dryRun = useBatchTaskResult<EmitEventTaskResult>(dryRunTaskId, 'emit-event');

  const blocker = submitBlocker(catalog, draft);
  const jsonError = entry && entry.context !== 'none' && draft.source === 'json' ? parseJsonObject(draft.jsonText).error : undefined;

  const errorText = (e: unknown) =>
    e instanceof ConsoleApiError
      ? ((e.body as { detail?: string } | undefined)?.detail ?? e.message)
      : (e as Error).message;

  const dispatch = (dryRunFlag: boolean) =>
    runBatch(
      grid,
      'emit_event',
      ctx.selection,
      buildParams(catalog!, draft, dryRunFlag) as unknown as Record<string, unknown>,
      ctx.filterQuery,
    );

  const dryRunMut = useMutation({
    mutationFn: () => dispatch(true),
    onSuccess: (r) => {
      setDryRunTaskId(r.task_id);
      if (r.capped) {
        snackbar.show(
          `Selection capped at ${r.processed.toLocaleString()} of ${r.matched.toLocaleString()} ${noun}s.`,
          'warning',
        );
      }
    },
    onError: (e) => snackbar.show(errorText(e), 'error'),
  });

  const emitMut = useMutation({
    mutationFn: () => dispatch(false),
    onSuccess: (r) => {
      snackbar.show(`Emitting ${entry?.code} for ${r.processed.toLocaleString()} ${noun}(s) in the background.`, 'success', {
        autoHideDuration: 15000,
        action: { label: 'View task', onClick: () => openBackgroundTask(r.task_id) },
      });
      ctx.clearSelection();
      ctx.close();
    },
    onError: (e) => {
      setConfirmOpen(false);
      snackbar.show(errorText(e), 'error');
    },
  });

  const busy = dryRunMut.isPending || emitMut.isPending;
  const result = dryRun.result;
  const wouldEmit = result?.counts.would_emit ?? 0;
  const webhooksOn = !!entry?.fires_webhooks && draft.webhooks;

  const pickEvent = (e: EmitEventCatalogEntry | null) =>
    patch({
      code: e?.code ?? null,
      source: 'empty',
      jsonText: e && e.context !== 'none' ? jsonTemplate(e) : '',
      webhooks: false,
      force: false,
    });

  return (
    <>
      <AppDialog
        open
        size="lg"
        onClose={busy ? () => undefined : ctx.close}
        disableBackdropClose
        title={
          <>
            Emit an event for {ctx.count.toLocaleString()} {noun}(s) <HelpLink story="emit-events" />
          </>
        }
        actions={
          <>
            <Button onClick={ctx.close} disabled={busy}>
              Cancel
            </Button>
            <Tooltip title={blocker ?? ''}>
              <span>
                <Button
                  variant="outlined"
                  onClick={() => dryRunMut.mutate()}
                  disabled={busy || blocker != null || dryRun.waitingForTask || dryRun.waitingForResult}
                >
                  Dry run
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={blocker ?? ''}>
              <span>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={() => setConfirmOpen(true)}
                  disabled={busy || blocker != null}
                >
                  Emit…
                </Button>
              </span>
            </Tooltip>
          </>
        }
      >
        <Stack spacing={2}>
          {catalogQuery.isLoading && <LinearProgress />}
          {catalogQuery.isError && (
            <Alert severity="error">The event catalog failed to load — try reopening the dialog.</Alert>
          )}
          {catalog && !catalog.enabled && (
            <Alert severity="error">Emitting events is disabled in this environment (ConsoleEmitEventsEnabled).</Alert>
          )}

          {catalog && (
            <>
              <Autocomplete
                options={catalog.events}
                groupBy={groupLabel}
                value={entry}
                onChange={(_, v) => pickEvent(v)}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(a, b) => a.code === b.code}
                renderOption={(props, o) => {
                  const { key, ...rest } = props as typeof props & { key: string };
                  return (
                    <li key={key} {...rest}>
                      <Box>
                        <Typography variant="body2">{o.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {o.code}
                          {o.timing && o.timing !== o.label ? ` · ${o.timing}` : ''}
                        </Typography>
                      </Box>
                    </li>
                  );
                }}
                renderInput={(params) => <TextField {...params} label="Event" size="small" required />}
              />

              {entry && (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip size="small" label={entry.code} />
                  {entry.typename !== entry.code && (
                    <Typography variant="caption" color="text.secondary">
                      built as <code>{entry.typename}</code>, exactly as the cron builds it
                    </Typography>
                  )}
                  {entry.timing && (
                    <Typography variant="caption" color="text.secondary">
                      normally emitted {entry.timing}
                    </Typography>
                  )}
                </Stack>
              )}

              {entry && entry.context !== 'none' && (
                <FormControl>
                  <FormLabel id="emit-source-label">
                    {entry.context === 'diff' ? 'Where does the diff come from?' : 'Where do the event’s values come from?'}
                  </FormLabel>
                  <RadioGroup
                    aria-labelledby="emit-source-label"
                    value={draft.source}
                    onChange={(e) => patch({ source: e.target.value as EmitEventSource })}
                  >
                    {(['empty', 'borrow', 'json'] as const).map((s) => (
                      <FormControlLabel
                        key={s}
                        value={s}
                        control={<Radio size="small" />}
                        label={SOURCE_LABELS[entry.context as 'diff' | 'params'][s](entry)}
                      />
                    ))}
                  </RadioGroup>

                  {draft.source === 'borrow' && (
                    <Stack spacing={1.5} sx={{ pl: 4, pt: 1 }}>
                      <Autocomplete
                        multiple
                        size="small"
                        options={catalog.statuses.map((s) => s.value)}
                        value={draft.statuses}
                        onChange={(_, v) => patch({ statuses: v })}
                        getOptionLabel={(v) => catalog.statuses.find((s) => s.value === v)?.label ?? String(v)}
                        renderInput={(params) => (
                          <TextField {...params} label="Only events with delivery status" placeholder="Any status" />
                        )}
                      />
                      <Box>
                        <TimeWindowPicker
                          keepRelative={false}
                          label="Emitted between (optional)"
                          value={draft.window}
                          onChange={(w) => patch({ window: w })}
                          allowOpenEnded
                        />
                        <Typography variant="caption" color="text.secondary" component="div">
                          Without a start, looks back {catalog.borrow.default_window_days} days from the end (or
                          now). Only events that already appear in Delivery Logs can be borrowed from.
                        </Typography>
                      </Box>
                      {entry.context === 'diff' && (
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={draft.skipEmpty}
                              onChange={(e) => patch({ skipEmpty: e.target.checked })}
                            />
                          }
                          label={`Skip updates whose diff is empty (checks up to ${catalog.borrow.max_candidates})`}
                        />
                      )}
                    </Stack>
                  )}

                  {draft.source === 'json' && (
                    <Box sx={{ pl: 4, pt: 1 }}>
                      <TextField
                        label={entry.context === 'diff' ? 'Diff (JSON)' : 'Values (JSON)'}
                        value={draft.jsonText}
                        onChange={(e) => patch({ jsonText: e.target.value })}
                        multiline
                        minRows={4}
                        maxRows={16}
                        fullWidth
                        size="small"
                        error={!!jsonError}
                        helperText={
                          jsonError ??
                          (entry.context === 'diff'
                            ? 'The archived diff shape, or just a "fields" list — the rest is derived. Paths are notification-context paths.'
                            : entry.params.map((p) => `${p.name}: ${p.description}`).join(' · '))
                        }
                        slotProps={{ htmlInput: { style: { fontFamily: 'monospace' }, spellCheck: false } }}
                      />
                      <Button size="small" onClick={() => patch({ jsonText: jsonTemplate(entry) })}>
                        Reset to the example
                      </Button>
                    </Box>
                  )}
                </FormControl>
              )}

              <TextField
                label="Tag"
                value={draft.tag}
                onChange={(e) => patch({ tag: e.target.value })}
                size="small"
                required
                fullWidth
                error={draft.tag.trim().length > catalog.tag.max_user_length}
                helperText={`${draft.tag.trim().length}/${catalog.tag.max_user_length} — every event is tagged ${catalog.tag.prefix}/<task id>/<tag>; find the run in Delivery Logs by it.`}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography variant="caption" color="text.secondary">
                          {catalog.tag.prefix}/&lt;task id&gt;/
                        </Typography>
                      </InputAdornment>
                    ),
                  },
                }}
              />

              {entry?.fires_webhooks && (
                <FormControlLabel
                  control={
                    <Checkbox size="small" checked={draft.webhooks} onChange={(e) => patch({ webhooks: e.target.checked })} />
                  }
                  label="Also trigger customer webhook subscriptions (Zapier) for this event"
                />
              )}
              {entry && entry.gates.length > 0 && (
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox size="small" checked={draft.force} onChange={(e) => patch({ force: e.target.checked })} />
                    }
                    label="Force — emit even where the event would normally be held back"
                  />
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ pl: 4 }}>
                    {entry.gates.map((g) => catalog.gates[g] ?? g).join(' ')} A tenant with events switched off never
                    emits.
                  </Typography>
                </Box>
              )}

              {ctx.selection.mode === 'all_matching' && (
                <Alert severity="info">
                  Acting on {ctx.matchedPhrase} {noun}s (at most {catalog.max_selection.toLocaleString()} per run).
                </Alert>
              )}

              <TextField
                label="Why are you emitting this event?"
                value={draft.reason}
                onChange={(e) => patch({ reason: e.target.value })}
                fullWidth
                size="small"
                multiline
                minRows={2}
                required
                error={draft.reason.length > 0 && draft.reason.trim().length < catalog.min_reason}
                helperText={
                  draft.reason.length > 0 && draft.reason.trim().length < catalog.min_reason
                    ? `At least ${catalog.min_reason} characters.`
                    : 'Recorded in the audit log with every emitted event.'
                }
              />

              <Alert severity="warning">
                Emit sends real notifications to guests{webhooksOn ? ' and triggers the tenants’ webhook subscriptions' : ''}.
                Use the dry run to see which notifications would match first.
              </Alert>

              {dryRun.waitingForTask && (
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Chip size="small" color="info" label="Dry run in progress" />
                    <Typography variant="caption" color="text.secondary">
                      Building every event and matching it against its notifications, without sending…
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant={dryRun.progressPct != null ? 'determinate' : 'indeterminate'}
                    value={dryRun.progressPct ?? 0}
                  />
                </Box>
              )}
              {dryRun.waitingForResult && <LinearProgress />}
              {dryRun.finished && !dryRun.succeeded && (
                <Alert severity="error">The dry run did not complete — check it in Background Tasks.</Alert>
              )}
              {result && dryRunTaskId && (
                <Box>
                  <Alert severity={wouldEmit > 0 ? 'success' : 'warning'} sx={{ mb: 1 }}>
                    <AlertTitle>
                      {wouldEmit > 0
                        ? `Dry run — ${wouldEmit.toLocaleString()} ${noun}(s) would emit`
                        : 'Dry run — nothing would be emitted'}
                    </AlertTitle>
                    Changing the event, tag, source or options voids this preview. Emit builds every event again from
                    the state at that moment.
                  </Alert>
                  <EmitEventsReport result={result} taskId={dryRunTaskId} />
                </Box>
              )}
            </>
          )}
        </Stack>
      </AppDialog>
      <ConfirmDialog
        open={confirmOpen}
        title="Emit events?"
        message={
          `Emit ${entry?.code ?? ''} for ${ctx.count.toLocaleString()} ${noun}(s)` +
          `${webhooksOn ? ', also triggering customer webhook subscriptions' : ''}` +
          `${entry && entry.gates.length > 0 && draft.force ? ', forcing past the event’s gates' : ''}. ` +
          'Real notifications will be sent. ' +
          (result ? '' : 'No dry run was reviewed. ') +
          'This runs in the background and every emission is written to the audit log.'
        }
        confirmLabel="Emit"
        destructive
        onConfirm={() => emitMut.mutate()}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}
